import { MapPin, FileText, CircleCheck, ArrowUpRight } from "lucide-react";
import { useEffect } from "react";

const JobCard = ({
  job,
  setJobList,
  isLoading,
  isSelected,
  onToggleSelect,
  resumeData,
  setJobDescription,
  navigateTo,
}) => {
  const matchScores = job.matchScores || {};
  const bestResume =
    Object.keys(matchScores).length > 0
      ? Object.keys(matchScores).reduce((a, b) =>
          matchScores[a] > matchScores[b] ? a : b,
        )
      : "";

  const displayScore = job.selectedResume
    ? matchScores[job.selectedResume]
    : Object.keys(matchScores).length > 0
      ? matchScores[bestResume]
      : null;

  useEffect(() => {
    if (!job.selectedResume && Object.keys(matchScores).length > 0) {
      setJobList((prev) =>
        prev.map((j) =>
          j.id === job.id ? { ...j, selectedResume: bestResume } : j,
        ),
      );
    }
  }, [job.id, job.selectedResume, bestResume, matchScores, setJobList]);

  const handleResumeChange = (resumeName) => {
    setJobList((prev) =>
      prev.map((j) =>
        j.id === job.id ? { ...j, selectedResume: resumeName } : j,
      ),
    );
  };

  const scoreColor =
    displayScore >= 80 ? "#84cc16" : displayScore >= 60 ? "#f59e0b" : "#ef4444";

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "14px",
        padding: "1.5rem",
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        border: isSelected ? "2px solid #402c7a" : "2px solid transparent",
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
        transition: "box-shadow 0.15s",
        position: "relative",
      }}
    >
      {/* Top row: title + score badge */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "1rem",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem" }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            disabled={isLoading}
            onChange={() => onToggleSelect(job.id)}
            style={{
              cursor: "pointer",
              accentColor: "#402c7a",
              marginTop: "4px",
              flexShrink: 0,
            }}
          />
          <div>
            <h3
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "#111827",
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              {job.title.split("(")[0].split(",")[0]}
            </h3>

            <p
              style={{
                fontSize: "0.9rem",
                color: "#6b7280",
                margin: "0.2rem 0 0",
              }}
            >
              {job.company}
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-center">
          {displayScore !== null && (
            <div
              style={{
                background: scoreColor,
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.9rem",
                borderRadius: "999px",
                padding: "0.3rem 0.85rem",
                flexShrink: 0,
                lineHeight: 1.4,
              }}
            >
              {displayScore}%
            </div>
          )}
          <button
            onClick={() => window.open(job.url)}
            className="buttons-navigation-mini "
          >
            <ArrowUpRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* File icon row */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        {job.tailored && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              fontSize: "0.78rem",
              color: "#16a34a",
              fontWeight: 500,
            }}
          >
            <CircleCheck size={13} /> Tailored
          </span>
        )}
      </div>

      {/* Description excerpt */}
      {job.description && (
        <p
          style={{
            fontSize: "0.875rem",
            color: "#6b7280",
            lineHeight: 1.6,
            margin: 0,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {job.description}
        </p>
      )}

      {/* Location + salary */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        <MapPin size={14} style={{ color: "#9ca3af", flexShrink: 0 }} />
        {job.location && (
          <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
            {job.location}
          </span>
        )}
        {job.salary && job.salary !== "Salary not specified" && (
          <span
            style={{
              fontSize: "0.78rem",
              fontWeight: 600,
              background: "#eef2ff",
              color: "#402c7a",
              padding: "0.2rem 0.65rem",
              borderRadius: "999px",
              border: "1px solid #c7d2fe",
            }}
          >
            {job.salary}
          </span>
        )}
      </div>

      {/* Resume selector */}
      {Object.keys(resumeData).length > 0 && (
        <div>
          <p
            style={{
              fontSize: "0.8rem",
              color: "#402c7a",
              fontWeight: 500,
              marginBottom: "0.2rem",
            }}
            className="text-center"
          >
            Select resume
          </p>
          <div className="buttons-container">
            <select
              value={job.selectedResume || bestResume}
              onChange={(e) => handleResumeChange(e.target.value)}
              className="dropdown-select"
            >
              {Object.keys(resumeData).map((name) => (
                <option key={name} value={name}>
                  {name}
                  {bestResume === name ? " - Best Match" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Apply Now button */}
      <button
        onClick={() =>
          navigateTo("tailor", false, null, { jobDescription: job.description })
        }
        disabled={job.url_valid === false}
        className="buttons-indigo w-full text-white"
      >
        {job.url_valid === false ? (
          "Listing Unavailable"
        ) : (
          <>Tailor for this job </>
        )}
      </button>
    </div>
  );
};

export default JobCard;
